import { useId, useRef, type KeyboardEvent, type ReactNode } from "react";
import { INFO_TRIGGER_CLASS } from "../lib/a11y";
import { cn } from "../lib/cn";
import { planYearPopover } from "../lib/format";
import type { PlanYears } from "../types/api";
import { InfoTooltip } from "./InfoTooltip";

export interface PlanYearToggleProps {
  /** The `GET /api/plans/years` payload; nothing renders while it is null or `next` is null. */
  years: PlanYears | null;
  /** The selected year, `years.current` or `years.next`; null reads as `years.next`, the default. */
  value: number | null;
  onChange: (year: number) => void;
  /** Set, the next-year segment is disabled and this text is its visible hint. */
  nextDisabledHint?: string | undefined;
  className?: string | undefined;
}

const ARROW_KEYS = new Set(["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"]);

/**
 * The two-segment plan-year control: the current year and next year marked as
 * an estimate, with an (i) trigger carrying the estimate explanation.
 *
 * It is a `radiogroup` of two `radio` buttons with a roving tabindex: Tab lands
 * on the checked segment, and any arrow key checks and focuses the other one.
 * A disabled next-year segment stays in the DOM as `aria-disabled` (not
 * `disabled`, so its hint stays discoverable), is described by the hint, and
 * is skipped by the arrow keys; the caller keeps `value` on the current year
 * meanwhile. Every label and the popover interpolate the payload's years, so
 * no year is a literal. Focus shows through the app-wide `:focus-visible`
 * outline.
 *
 * The type token sits on the group rather than in the segments' `cn()` call,
 * because the custom `text-*` size tokens share a namespace with the color
 * utilities and tailwind-merge would drop one.
 */
export function PlanYearToggle({
  years,
  value,
  onChange,
  nextDisabledHint,
  className,
}: PlanYearToggleProps): ReactNode {
  const hintId = useId();
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);
  if (years === null || years.next === null) return null;
  const selected = value ?? years.next;
  const segments = [
    { year: years.current, label: `${years.current}`, disabled: false },
    { year: years.next, label: `${years.next} (est.)`, disabled: nextDisabledHint !== undefined },
  ];

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
    if (!ARROW_KEYS.has(event.key)) return;
    event.preventDefault();
    const other = index === 0 ? 1 : 0;
    const target = segments[other];
    if (target === undefined || target.disabled) return;
    onChange(target.year);
    buttons.current[other]?.focus();
  };

  return (
    <div className={cn("flex flex-wrap items-center gap-x-2 gap-y-1", className)}>
      <div
        role="radiogroup"
        aria-label="Plan year"
        className="border-haze bg-ice text-body-sm inline-flex rounded-lg border p-0.5 font-semibold"
      >
        {segments.map((segment, index) => (
          <button
            key={segment.year}
            ref={(el) => {
              buttons.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={selected === segment.year}
            aria-disabled={segment.disabled || undefined}
            aria-describedby={segment.disabled ? hintId : undefined}
            tabIndex={selected === segment.year ? 0 : -1}
            onClick={() => {
              if (!segment.disabled) onChange(segment.year);
            }}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={cn(
              "min-h-11 whitespace-nowrap rounded-md px-4 transition-colors",
              selected === segment.year
                ? "text-midnight bg-white shadow-sm"
                : "text-slate-teal hover:text-midnight",
              segment.disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
            )}
          >
            {segment.label}
          </button>
        ))}
      </div>
      <InfoTooltip
        text={planYearPopover(years)}
        label="About the plan-year estimate"
        triggerClassName={INFO_TRIGGER_CLASS}
        panelClassName="w-72"
      />
      {nextDisabledHint !== undefined && (
        <p id={hintId} className="text-caption text-slate-teal basis-full">
          {nextDisabledHint}
        </p>
      )}
    </div>
  );
}
